import React from 'react';
import { Users, CheckCircle, Clock, ExternalLink, Download, Eye, TrendingUp } from 'lucide-react';

const ClasseCard = ({ groupe, onVoirBulletin, onVoirTous, onDownload }) => {
  const tauxValidation = groupe.total > 0 ? Math.round((groupe.valides / groupe.total) * 100) : 0;
  
  // Déterminer la couleur du taux de validation
  const getTauxColor = (taux) => {
    if (taux >= 80) return 'text-green-600 bg-green-100 border-green-200';
    if (taux >= 60) return 'text-blue-600 bg-blue-100 border-blue-200';
    if (taux >= 40) return 'text-yellow-600 bg-yellow-100 border-yellow-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 hover:border-[#202654]/20 group">
      {/* En-tête de la carte */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#202654] transition-colors">
            {groupe.classe?.libelle || 'Classe inconnue'}
          </h4>
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            <p className="text-sm text-gray-600">
              {groupe.classe?.niveau?.libelle || 'Niveau inconnu'}
            </p>
            {groupe.classe?.effectif && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {groupe.classe.effectif} élèves
              </span>
            )}
          </div>
        </div>
        <div className="p-3 bg-gradient-to-br from-[#202654] to-[#374151] rounded-lg shadow-md group-hover:shadow-lg transition-shadow">
          <Users className="h-5 w-5 text-white" />
        </div>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="text-center p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
          <div className="text-xl font-bold text-gray-900">{groupe.total}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200 hover:border-green-300 transition-colors">
          <div className="text-xl font-bold text-green-600 flex items-center justify-center">
            <CheckCircle className="h-4 w-4 mr-1" />
            {groupe.valides}
          </div>
          <div className="text-xs text-green-700">Validés</div>
        </div>
        <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200 hover:border-yellow-300 transition-colors">
          <div className="text-xl font-bold text-yellow-600 flex items-center justify-center">
            <Clock className="h-4 w-4 mr-1" />
            {groupe.en_attente}
          </div>
          <div className="text-xs text-yellow-700">En attente</div>
        </div>
      </div>

      {/* Taux de validation */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Taux de validation</span>
          <span className={`text-sm font-semibold px-2 py-1 rounded-full border ${getTauxColor(tauxValidation)}`}>
            {tauxValidation}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              tauxValidation >= 80 ? 'bg-green-500' : 
              tauxValidation >= 60 ? 'bg-blue-500' : 
              tauxValidation >= 40 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${tauxValidation}%` }}
          ></div>
        </div>
      </div>

      {/* Liste des bulletins récents */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Bulletins récents</span>
          <span className="text-xs text-gray-500">
            {groupe.bulletins.length > 3 ? `+${groupe.bulletins.length - 3} autres` : ''}
          </span>
        </div>
        
        <div className="max-h-32 overflow-y-auto space-y-2">
          {groupe.bulletins.slice(0, 3).map((bulletin, bulletinIndex) => (
            <div 
              key={bulletin.id || `bulletin-${bulletinIndex}`} 
              className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors group/bulletin"
            >
              <div className="text-sm flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {bulletin.etudiant?.nom && bulletin.etudiant?.prenom 
                    ? `${bulletin.etudiant.nom} ${bulletin.etudiant.prenom}`
                    : 'Étudiant inconnu'
                  }
                </div>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    bulletin.est_valide 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                  }`}>
                    {bulletin.est_valide ? 'Validé' : 'En attente'}
                  </span>
                  {bulletin.moyenne_semestre?.moyenne_generale && (
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                      {bulletin.moyenne_semestre.moyenne_generale}/20
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => onVoirBulletin(bulletin.id)}
                className="p-1.5 text-[#202654] hover:bg-[#202654]/10 rounded-lg transition-colors opacity-0 group-hover/bulletin:opacity-100"
                title="Voir le bulletin"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {groupe.bulletins.length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
              Aucun bulletin disponible
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex space-x-2 pt-3 border-t border-gray-200">
        <button 
          onClick={() => onVoirTous(groupe.classe?.id)}
          className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-r from-[#202654] to-[#374151] text-white rounded-lg hover:from-[#374151] hover:to-[#202654] transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
        >
          <Eye className="h-4 w-4 mr-1" />
          Voir Tous
        </button>
        <button 
          onClick={() => onDownload(groupe.classe?.id)}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors shadow-sm hover:shadow-md"
          title="Télécharger tous les bulletins"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>

      {/* Indicateur de performance */}
      {tauxValidation >= 80 && (
        <div className="absolute top-4 right-4">
          <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
            <TrendingUp className="h-3 w-3 mr-1" />
            Excellent
          </div>
        </div>
      )}
    </div>
  );
};

export default ClasseCard;
